import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Typography, FormControlLabel, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Button, CheckBox as Checkbox, TextField } from '@classity-edtech/ui-base';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import { MessageBubblesIcon } from '@classity-edtech/ui-base/dist/components/Displays';

import { resetFlashMessage, updateFlashMessage } from '../../store/actions/flashMessage';
import { useAppDispatch } from '../../store/hooks';

export interface SpaceType {
  label: string;
  value: string;
}

export interface ContactFormProps {
  className?: string;
  spaceTypes?: SpaceType[];
  onSubmit?: (data: ContactFormData) => void;
}

export interface ContactFormData {
  selectedSpaceTypes: string[];
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  enquiry: string;
}

const defaultSpaceTypes: SpaceType[] = [
  { label: 'Small Business & Startup', value: 'small_business' },
  { label: 'Private Office', value: 'private_office' },
  { label: 'Dedicated Desk', value: 'dedicated_desk' },
  { label: 'Hot Desk', value: 'hot_desk' },
  { label: 'Booth', value: 'booth' },
  { label: 'Meeting Room', value: 'meeting_room' },
  { label: 'Training Room', value: 'training_room' },
  { label: 'Virtual Office', value: 'virtual_office' },
];

export const ContactForm = ({
  className,
  spaceTypes = defaultSpaceTypes,
  onSubmit,
}: ContactFormProps) => {
  const screens = useAppMediaQuery();
  const isMobile = screens.isMobile || screens.isTablet;
  const dispatch: any = useAppDispatch();

  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [enquiry, setEnquiry] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCheckboxChange = (value: string) => {
    setSelectedTypes(prev =>
      prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value],
    );
  };

  const resetForm = () => {
    setSelectedTypes([]);
    setFirstName('');
    setLastName('');
    setPhone('');
    setEmail('');
    setEnquiry('');
  };

  const buildMailtoLink = (formData: ContactFormData) => {
    const subject = encodeURIComponent('Spaces - Enquiries');
    const body = encodeURIComponent(
      [
        `Name: ${formData.firstName} ${formData.lastName}`,
        `Phone: ${formData.phone || 'Not provided'}`,
        `Email: ${formData.email || 'Not provided'}`,
        `Space types: ${formData.selectedSpaceTypes.join(', ') || 'Not provided'}`,
        '',
        `Enquiry:\n${formData.enquiry}`,
      ].join('\n'),
    );

    return `mailto:spaces@classity.co?subject=${subject}&body=${body}`;
  };

  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) {
      return;
    }

    const formData: ContactFormData = {
      selectedSpaceTypes: selectedTypes,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      phone: phone.trim(),
      email: email.trim(),
      enquiry: enquiry.trim(),
    };

    setIsSubmitting(true);

    if (onSubmit) {
      onSubmit(formData);
    }

    try {
      const response = await fetch('https://formsubmit.co/ajax/spaces@classity.co', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        body: new URLSearchParams({
          _replyto: formData.email,
          _subject: 'Spaces - Enquiries',
          _captcha: 'false',
          _template: 'table',
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          email: formData.email,
          enquiry: formData.enquiry,
          selectedSpaceTypes: formData.selectedSpaceTypes.join(', '),
        }).toString(),
      });

      const result = await response.json().catch(() => ({}));
      const isFormSubmitSuccess = result?.success === true || result?.success === 'true';

      if (isFormSubmitSuccess) {
        dispatch(
          updateFlashMessage({
            open: true,
            severity: 'success',
            message:
              'We appreciate your reaching out to us. One of our team members will get back to you shortly.',
          }),
        );
      } else {
        dispatch(
          updateFlashMessage({
            open: true,
            severity: 'warning',
            message: 'Your request could not be processed.',
            icon: <MessageBubblesIcon sx={{ width: '16px', height: '16px' }} />,
            secondaryAction: {
              label: 'Send an email',
              onClick: () => {
                window.location.href = buildMailtoLink(formData);
              },
            },
            primaryAction: {
              label: 'Call us',
              onClick: () => {
                window.open('https://wa.me/2348065957778', '_blank');
              },
            },
          }),
        );
      }
    } catch (error) {
      dispatch(
        updateFlashMessage({
          open: true,
          severity: 'warning',
          message: 'Your request could not be processed.',
          icon: <MessageBubblesIcon sx={{ width: '16px', height: '16px' }} />,
          secondaryAction: {
            label: 'Send an email',
            onClick: () => {
              window.location.href = buildMailtoLink(formData);
            },
          },
          primaryAction: {
            label: 'Call us',
            onClick: () => {
              window.open('https://wa.me/2348065957778', '_blank');
            },
          },
        }),
      );
    } finally {
      resetForm();
      setIsSubmitting(false);
      setTimeout(() => dispatch(resetFlashMessage()), 30000);
    }
  };

  const isFormValid = Boolean(
    firstName.trim() && lastName.trim() && email.trim() && enquiry.trim(),
  );

  // Split space types into rows
  const getRows = () => {
    if (isMobile) {
      // Mobile: 2 items per column, stacked vertically
      return [
        spaceTypes.slice(0, 2),
        spaceTypes.slice(2, 4),
        spaceTypes.slice(4, 6),
        spaceTypes.slice(6, 8),
      ];
    } else {
      // Desktop: 2 items per row, side by side
      return [
        spaceTypes.slice(0, 2),
        spaceTypes.slice(2, 4),
        spaceTypes.slice(4, 6),
        spaceTypes.slice(6, 8),
      ];
    }
  };

  return (
    <Box
      className={className}
      sx={{
        backgroundColor: '#F4F4F4',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        py: 5,
        px: { xs: 2.5, md: 0 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
          width: { xs: '100%', md: '568px' },
          maxWidth: { xs: '100%', md: '568px' },
        }}
      >
        {/* Space Types Checkboxes */}
        <Stack spacing={2}>
          {getRows().map((row, rowIndex) => (
            <Stack
              key={rowIndex}
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              sx={{ width: '100%' }}
            >
              {row.map(spaceType => (
                <Box
                  key={spaceType.value}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    overflow: 'hidden',
                    flex: { xs: 'initial', md: '1 0 0' },
                    minWidth: { xs: 'auto', md: 0 },
                    width: { xs: '100%', md: 'auto' },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedTypes.includes(spaceType.value)}
                        onChange={() => handleCheckboxChange(spaceType.value)}
                        sx={{
                          padding: 0,
                          // '& .MuiSvgIcon-root': {
                          //   backgroundColor: '#FFFFFF',
                          // },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant='body2'
                        sx={{
                          color: globalColours['$global-colour-content-primary'],
                          whiteSpace: 'nowrap',
                          ml: 1,
                        }}
                      >
                        {spaceType.label}
                      </Typography>
                    }
                    sx={{ margin: 0 }}
                  />
                </Box>
              ))}
            </Stack>
          ))}
        </Stack>

        {/* Spacer */}
        <Box sx={{ height: '24px' }} />

        {/* Name Fields */}
        <Stack direction='row' spacing={2} sx={{ width: '100%' }}>
          <Box sx={{ flex: '1 0 0', minWidth: 0 }}>
            <Typography
              variant='body3'
              sx={{
                color: globalColours['$global-colour-content-secondary'],
                mb: 0.375,
              }}
            >
              First Name
            </Typography>
            <TextField
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#FFFFFF',
                  borderRadius: '10px',
                  '& fieldset': {
                    borderColor: '#CDCDD1',
                  },
                  '&:hover fieldset': {
                    borderColor: '#CDCDD1',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#CDCDD1',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '12px 16px',
                  height: '24px',
                  backgroundColor: '#FFFFFF',
                },
                // '& .MuiInputBase-root': {
                //   backgroundColor: '#FFFFFF',
                // },
                // '& .MuiInputBase-input': {
                //   backgroundColor: '#FFFFFF',
                // },
              }}
            />
          </Box>
          <Box sx={{ flex: '1 0 0', minWidth: 0 }}>
            <Typography
              variant='body3'
              sx={{
                color: globalColours['$global-colour-content-secondary'],
                mb: 0.375,
              }}
            >
              Last Name
            </Typography>
            <TextField
              value={lastName}
              onChange={e => setLastName(e.target.value)}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#FFFFFF',
                  borderRadius: '10px',
                  '& fieldset': {
                    borderColor: '#CDCDD1',
                  },
                  '&:hover fieldset': {
                    borderColor: '#CDCDD1',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#CDCDD1',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '12px 16px',
                  height: '24px',
                },
              }}
            />
          </Box>
        </Stack>

        {/* Spacer */}
        <Box sx={{ height: '16px' }} />

        {/* Contact Fields */}
        <Stack direction='row' spacing={2} sx={{ width: '100%' }}>
          <Box sx={{ flex: '1 0 0', minWidth: 0 }}>
            <Typography
              variant='body3'
              sx={{
                color: globalColours['$global-colour-content-secondary'],
                mb: 0.375,
              }}
            >
              Phone
            </Typography>
            <TextField
              value={phone}
              onChange={e => setPhone(e.target.value)}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#FFFFFF',
                  borderRadius: '10px',
                  '& fieldset': {
                    borderColor: '#CDCDD1',
                  },
                  '&:hover fieldset': {
                    borderColor: '#CDCDD1',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#CDCDD1',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '12px 16px',
                  height: '24px',
                },
              }}
            />
          </Box>
          <Box sx={{ flex: '1 0 0', minWidth: 0 }}>
            <Typography
              variant='body3'
              sx={{
                color: globalColours['$global-colour-content-secondary'],
                mb: 0.375,
              }}
            >
              Email
            </Typography>
            <TextField
              value={email}
              onChange={e => setEmail(e.target.value)}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#FFFFFF',
                  borderRadius: '10px',
                  '& fieldset': {
                    borderColor: '#CDCDD1',
                  },
                  '&:hover fieldset': {
                    borderColor: '#CDCDD1',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#CDCDD1',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  padding: '12px 16px',
                  height: '24px',
                },
              }}
            />
          </Box>
        </Stack>

        {/* Spacer */}
        <Box sx={{ height: '16px' }} />

        {/* Enquiry Field */}
        <Box sx={{ width: '100%' }}>
          <Typography
            variant='body3'
            sx={{
              color: globalColours['$global-colour-content-secondary'],
              mb: 0.375,
            }}
          >
            Your Enquiry
          </Typography>
          <TextField
            value={enquiry}
            onChange={e => setEnquiry(e.target.value)}
            fullWidth
            type='textarea'
            rows={4}
            placeholder='Provide details of your enquiry...'
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#FFFFFF',
                borderRadius: '10px',
                '& fieldset': {
                  borderColor: '#CDCDD1',
                },
                '&:hover fieldset': {
                  borderColor: '#CDCDD1',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#CDCDD1',
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '12px 16px',
                fontFamily: 'Gill Sans, sans-serif',
                fontSize: '14px',
                fontWeight: 300,
                lineHeight: '22px',
                letterSpacing: '0.5px',
                color: '#0A0A0B',
              },
            }}
          />
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              mt: 1.625,
              width: '100%',
            }}
          >
            <Typography
              variant='body3'
              sx={{
                flex: '1 0 0',
                minWidth: 0,
                color: globalColours['$global-colour-content-secondary'],
              }}
            >
              Reserve a space or for general enquiries.
            </Typography>
            <Typography
              variant='body3'
              sx={{
                color: globalColours['$global-colour-content-secondary'],
                textAlign: 'center',
                whiteSpace: 'nowrap',
              }}
            >
              {enquiry.length}/500
            </Typography>
          </Box>
        </Box>

        {/* Submit Button */}
        <Box sx={{ width: '100%' }}>
          <Box sx={{ height: '32px' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box sx={{ flex: '1 0 0', minWidth: 0, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                onClick={handleSubmit}
                disabled={!isFormValid}
                disableBottomFixed
                sx={{
                  backgroundColor: isFormValid ? '#D22F11' : '#CDCDD1',
                  color: isFormValid ? '#FFFFFF' : '#686871',
                  px: 3,
                  py: 1.5,
                  borderRadius: '100px',
                  flex: '1 0 0',
                  minWidth: 0,
                  '&:hover': {
                    backgroundColor: isFormValid ? '#B32710' : '#CDCDD1',
                  },
                  '&:disabled': {
                    backgroundColor: '#CDCDD1',
                    color: '#686871',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Gill Sans, sans-serif',
                    fontSize: '16px',
                    fontWeight: 600,
                    lineHeight: '24px',
                    letterSpacing: '-0.2px',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Send Enquiry
                </Typography>
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

ContactForm.propTypes = {
  className: PropTypes.string,
  spaceTypes: PropTypes.array,
  onSubmit: PropTypes.func,
};

ContactForm.defaultProps = {
  className: null,
  spaceTypes: defaultSpaceTypes,
};

export default styled<any>(ContactForm)(({ theme }) => ({
  '& .MuiInputBase-root': {
    backgroundColor: '#FFFFFF',
  },
  '& .MuiInputBase-input': {
    backgroundColor: '#FFFFFF',
  },
}));
